import { useMemo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { FloatingPortal } from '@floating-ui/react';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { useIsSmallMode } from '@frontend/shared';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverDialog, usePopoverDialog } from '@frontend/design-system';
import { useOnboardingShallowStore } from '../hooks/use-onboarding-store';

/**
 * Renders the onboarding guide UI — a trigger button (e.g., sidebar nav item) and
 * a popover dialog (e.g., checklist) for the highest-priority incomplete flow.
 *
 * Only flows that have already passed their eligibility checks and called `registerFlow`
 * will appear in the store's `flows` array. This component simply selects the most
 * important one that still has work to do.
 *
 * Each flow provides its own `NavContent` and `TaskContent` components, so the
 * visual presentation is fully delegated to the flow definition.
 */
export function OnboardingGuide() {
  const { flows, completedHotspots, isGuideOpen, toggleOnboardingGuide } = useOnboardingShallowStore(
    'flows',
    'completedHotspots',
    'isGuideOpen',
    'toggleOnboardingGuide'
  );

  const checklistSeenTrackedForOpen = useRef(false);

  const { isSmallMode } = useIsSmallMode();

  const { getDialogProps, getTriggerProps, open, close } = usePopoverDialog({
    placement: isSmallMode ? 'top-end' : 'right-start',
    onOpen: () => !isGuideOpen && toggleOnboardingGuide(),
    onClose: () => isGuideOpen && toggleOnboardingGuide(),
    middlewareOptions: {
      offset: {
        mainAxis: isSmallMode ? 16 : 0,
        alignmentAxis: isSmallMode ? 68 : 8,
      },
    },
  });

  const { chats } = useMiniChatShallowStore('chats', 'removeChat');

  const activeFlow = useMemo(() => {
    if (flows.length === 0) return null;
    return [...flows].sort((a, b) => b.priority - a.priority)[0];
  }, [flows]);

  const hasIncompleteSteps = useMemo(
    () => !!activeFlow?.hotspots.some((hotspot) => hotspot.kind !== 'info' && !completedHotspots.has(hotspot.key)),
    [activeFlow, completedHotspots]
  );

  useEffect(() => {
    if (isGuideOpen) open();
    else close();
  }, [isGuideOpen, open, close]);

  useEffect(() => {
    if (!isGuideOpen) {
      checklistSeenTrackedForOpen.current = false;
      return;
    }

    const checklistSeenEvent = activeFlow?.tracking?.checklistSeenEvent;
    if (!checklistSeenEvent) return;
    if (checklistSeenTrackedForOpen.current) return;

    checklistSeenTrackedForOpen.current = true;

    pendo.track(checklistSeenEvent, {
      product: 'call-intelligence',
      onboardingFlow: activeFlow.id,
    });
  }, [isGuideOpen, activeFlow]);

  if (!activeFlow?.NavContent) return null;

  const { NavContent, TaskContent } = activeFlow;

  const content = (
    <>
      <NakedButton
        {...getTriggerProps()}
        css={isSmallMode ? styles.floatingTrigger(isGuideOpen, chats.length > 0) : styles.trigger}
        data-trackingid={activeFlow.tracking?.guideTriggerTrackingId}
      >
        <NavContent
          isOpen={isGuideOpen}
          hasIncompleteSteps={hasIncompleteSteps}
          completedHotspots={completedHotspots}
          flow={activeFlow}
        />
      </NakedButton>

      {TaskContent && (
        <PopoverDialog {...getDialogProps()} css={styles.dialog}>
          <TaskContent onClose={toggleOnboardingGuide} completedHotspots={completedHotspots} flow={activeFlow} />
        </PopoverDialog>
      )}
    </>
  );

  if (isSmallMode) {
    return <FloatingPortal>{content}</FloatingPortal>;
  }

  return content;
}

const styles = {
  trigger: css({
    cursor: 'pointer',
    display: 'flex',
  }),

  dialog: css({
    padding: 0,
  }),

  floatingTrigger: (isGuideOpen: boolean, hasChats: boolean) => {
    const borderColor = isGuideOpen ? theme.colorsV2.seaweed50(10) : theme.colors.neutral20;

    return css({
      position: 'absolute',
      right: 0,
      bottom: hasChats ? theme.spacing(6) : 0,
      height: theme.spacing(5),
      width: theme.spacing(6.5),
      minWidth: theme.spacing(6.5),
      padding: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      backgroundColor: isGuideOpen ? theme.colorsV2.seaweed50(10) : theme.colors.white,
      borderTop: `1px solid ${borderColor}`,
      borderLeft: `1px solid ${borderColor}`,
      borderBottom: hasChats ? `1px solid ${borderColor}` : 'none',
      borderTopLeftRadius: theme.borderRadius.medium,
      borderBottomLeftRadius: hasChats ? theme.borderRadius.medium : undefined,
      '&:hover': {
        backgroundColor: theme.colors.neutral5,
      },
    });
  },
};
